export const ROLE_OPTIONS = [
  { value: "user_view", label: "My View" },
  { value: "company_view", label: "Company View" },
];

export const PERIOD_PRESET_FILTERS = [
  { label: "Today", days: 0 },
  { label: "Yesterday", days: 1, isSingleDay: true },
  { label: "1 Week", days: 7 },
  { label: "30 Days", days: 30 },
];

export const toIsoDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const pct = (part, total) => (total ? Number(((part / total) * 100).toFixed(1)) : 0);

export const formatRelativeTime = (timestamp, now) => {
  if (!timestamp) return "—";
  const diffSeconds = Math.max(0, Math.round((now - timestamp) / 1000));
  if (diffSeconds < 10) return "just now";
  if (diffSeconds < 60) return `${diffSeconds}s ago`;
  const diffMinutes = Math.round(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.round(diffHours / 24);
  return `${diffDays}d ago`;
};

export const formatShortDate = (isoDate) => {
  if (!isoDate) return "";
  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export const formatDateRange = (startDate, endDate) => {
  const start = formatShortDate(startDate);
  const end = formatShortDate(endDate);
  if (!start || !end) return "";
  return `${start} - ${end}`;
};

export const formatBucketRangeLabel = (granularity, startDate, endDate) => {
  if (granularity === "month") return "";
  if (granularity === "day") return formatShortDate(startDate);
  return formatDateRange(startDate, endDate);
};

export const transformIndentSummary = (apiData) => ({
  kpis: {
    raised: apiData.total_indent_raised,
    accepted: apiData.accepted,
    acceptedPct: pct(apiData.accepted, apiData.total_indent_raised),
    rejected: apiData.rejected,
    rejectedPct: pct(apiData.rejected, apiData.total_indent_raised),
    pending: apiData.pending,
    pendingPct: pct(apiData.pending, apiData.total_indent_raised),
    movedToSpot: apiData.moved_from_spot,
    movedToSpotPct: pct(apiData.moved_from_spot, apiData.total_indent_raised),
    expired: apiData.expired,
    expiredPct: pct(apiData.expired, apiData.total_indent_raised),
    cancelled: apiData.cancelled,
    cancelledPct: pct(apiData.cancelled, apiData.total_indent_raised),
  },
  raisedByPeriodMessage: apiData.indents_raised?.message || "",
  raisedByPeriod: apiData.indents_raised?.buckets && apiData.indents_raised.buckets.length
    ? apiData.indents_raised.buckets.map((b) => ({
        label: b.label,
        rangeLabel: formatBucketRangeLabel(apiData.indents_raised.granularity, b.start_date, b.end_date),
        contracted: b.contracted_count,
        spot: b.spot_count,
        spotSharePct: b.spot_share_pct,
      }))
    : apiData.indents_raised?.message
      ? []
    : [
        {
          label: "Selected period",
          rangeLabel: "",
          contracted: apiData.contracted_count,
          spot: apiData.spot_count,
          spotSharePct: apiData.spot_share_pct,
        },
      ],
  byPlant: (apiData.by_plant || []).map((p) => {
    const shortLocation = typeof p.plant === "string" ? p.plant : p.plant?.location?.location || p.plant?.name || "Unknown";
    const fullLocation = typeof p.plant === "string" ? p.plant : p.plant?.location?.label || p.plant?.location_name || shortLocation;
    const code = typeof p.plant === "string" ? "" : p.plant?.location_code || "";
    return {
      name: code ? `${code} - ${shortLocation}` : shortLocation,
      fullName: code ? `${code} - ${fullLocation}` : fullLocation,
      locationName: fullLocation,
      code,
      address: typeof p.plant === "string" ? "" : p.plant?.addressLine1 || "",
      isOthers: p.plant === "Others",
      accepted: p.accepted,
      rejected: p.rejected,
      pending: p.pending,
      movedToSpot: p.moved_from_spot,
      total: p.total,
    };
  }),
  byTransporter: (apiData.by_transporter || []).map((t) => ({
    name: t.transporter_name,
    transporterId: t.transporter_id,
    isOthers: t.transporter_id === "OTHERS",
    accepted: t.accepted,
    rejected: t.rejected,
    pending: t.pending,
    movedToSpot: t.moved_from_spot,
    total: t.total,
  })),
});

export const normalizeLocation = (loc) => ({
  key: loc?.location?.label || loc?.location_name || loc?.location_code || "Unknown",
  name: loc?.location?.label || loc?.location_name || "Unknown",
  address: loc?.addressLine1 || loc?.location_name || "",
  code: loc?.location_code || "",
});

export const pivotDispatchMatrix = (dispatchMatrix) => {
  const originsMap = new Map();
  const destinationsMap = new Map();
  (dispatchMatrix || []).forEach((cell) => {
    const o = normalizeLocation(cell.origin);
    const d = normalizeLocation(cell.destination);
    originsMap.set(o.key, o);
    destinationsMap.set(d.key, d);
  });
  const destinations = Array.from(destinationsMap.values());
  const origins = Array.from(originsMap.values());

  const rows = origins.map((origin) => {
    const cells = {};
    let total = 0;
    (dispatchMatrix || [])
      .filter((c) => normalizeLocation(c.origin).key === origin.key)
      .forEach((c) => {
        const d = normalizeLocation(c.destination);
        cells[d.key] = (cells[d.key] || 0) + c.count;
        total += c.count;
      });
    return { ...origin, cells, total };
  });

  const matrixTotals = {};
  let matrixGrandTotal = 0;
  destinations.forEach((dest) => {
    const destTotal = (dispatchMatrix || [])
      .filter((c) => normalizeLocation(c.destination).key === dest.key)
      .reduce((sum, c) => sum + c.count, 0);
    matrixTotals[dest.key] = destTotal;
    matrixGrandTotal += destTotal;
  });

  return { destinations, rows, matrixTotals, matrixGrandTotal };
};

export const transformModelsByDestination = (modelsByDestination) => {
  const byDestination = modelsByDestination?.by_destination || [];
  const modelsSet = new Set();
  const rows = byDestination.map((entry) => {
    const segments = {};
    let total = 0;
    (entry.models || []).forEach((m) => {
      modelsSet.add(m.material_name);
      segments[m.material_name] = m.total_quantity;
      total += m.total_quantity;
    });
    return { destination: normalizeLocation(entry.destination), total, segments };
  });
  return { models: Array.from(modelsSet), rows };
};

export const transformVehicleAllocation = (apiData) => {
  const { destinations, rows, matrixTotals, matrixGrandTotal } = pivotDispatchMatrix(
    apiData.dispatch_matrix
  );
  return {
    statusTiles: apiData.status_tiles || {},
    destinations,
    dispatchMatrix: rows,
    matrixTotals,
    matrixGrandTotal,
    modelsByDestination: transformModelsByDestination(apiData.models_by_destination),
  };
};
/*
  Append these to transformers.js. They rely on `pct` which already exists
  there. Keeping them in one place because the mockup keeps contracted vs
  non-contracted as the single organizing idea across every card.
*/

export const transformSpotMetrics = (apiData) => {
  if (!apiData) return null;
  const {
    request_raised = 0,
    total_spend = 0,
    net_savings = 0,
    net_escalation = 0,
    contracted_spots = 0,
    contracted_perc = 0,
    contracted_spend = 0,
    contracted_saving = 0,
    non_contracted_spots = 0,
    non_contracted_perc = 0,
    non_contracted_spend = 0,
    non_contracted_saving = 0,
    truck_requested_load = 0,
    enquiry_with_no_quote = 0,
    avg_quotes = 0,
    avg_time_first_quote = 0,
    quotes_received = 0,
  } = apiData;

  // ASSUMPTION (needs confirming): net saving is (net_savings - net_escalation),
  // positive = saved overall, negative = overspent overall. Flip this if
  // net_savings already comes back signed from the backend.
  const netAmount = net_savings;

  return {
    spend: {
      total: total_spend,
      contracted: contracted_spend,
      nonContracted: non_contracted_spend,
      contractedPct: contracted_spend
        ? Number(((contracted_spend / total_spend) * 100).toFixed(1))
        : 0,
      nonContractedPct: non_contracted_spend
        ? Number(((non_contracted_spend / total_spend) * 100).toFixed(1))
        : 0,
    },
    netSaving: {
      amount: netAmount,
      isSaving: netAmount >= 0,
      contracted: contracted_saving,
      nonContracted: non_contracted_saving,
    },
    enquiries: {
      raised: request_raised,
      contractedSpots: contracted_spots,
      nonContractedSpots: non_contracted_spots,
      contractedPerc: contracted_perc,
      nonContractedPerc: non_contracted_perc,
      truckloadsRequested: truck_requested_load,
    },
    quotes: {
      avgQuotes: avg_quotes,
      avgTimeToFirstQuote: avg_time_first_quote,
      quotesReceived: quotes_received,
      noQuoteCount: enquiry_with_no_quote,
    },
  };
};

export const transformSpotFunnel = (apiData) => {
  if (!apiData) return null;
  const {
    raised = 0,
    raised_truck_load = 0,
    received_quote = 0,
    received_quote_truck_load = 0,
    approved_quote = 0,
    approved_quote_truck_load = 0,
    moved_to_indent = 0,
    moved_to_indent_truck_load = 0,
    cancelled_spot = 0,
    expired_spot = 0,
  } = apiData;

  const stageOf = (label, enqValue, truckValue, dropLabel) => ({
    label,
    enquiries: enqValue,
    truckloads: truckValue,
    dropLabel,
  });

  const stages = [
    stageOf("Spot raised", raised, raised_truck_load, null),
    stageOf("Quoted", received_quote, received_quote_truck_load, "got no quotes"),
    stageOf("Approved", approved_quote, approved_quote_truck_load, "not approved"),
    stageOf("Moved to indent", moved_to_indent, moved_to_indent_truck_load, "lapsed before indent"),
  ];

  stages.cancelled = cancelled_spot;
  stages.expired = expired_spot;

  return stages;
};

export const transformTopTransporters = (apiData) => {
  if (!apiData) return { rows: [], total: 0 };
  const rows = (apiData.transporter_data || []).map((t) => ({
    transporterId: t.transporter_id,
    name: t.transporter_name,
    spend: t.spot_spend || 0,
    sharePct: t.share_spend || 0,
    savings: t.savings || 0,
    savingsPct: t.savings_perc || 0,
  }));
  return { rows: rows.sort((a, b) => b.spend - a.spend), total: apiData.total || rows.length };
};

const _plantLabel = (origin) => {
  if (!origin) return "Unknown";
  return (
    origin.location_name ||
    (origin.location && (origin.location.location || origin.location.label)) ||
    "Unknown"
  );
};

// Derives "Business Share / Savings: By Plant" from /spot_overview/lanes —
// there's no dedicated by-plant endpoint, so we group the (already-fetched,
// unpaginated) lane list by origin ourselves. spend_perc/savings_perc on
// each lane are shares of the GRAND TOTAL across all lanes, which is
// exactly the same total a per-plant share should be measured against, so
// summing spend/savings per plant and recomputing share against the same
// grand total is consistent with how the transporter leaderboard computes
// its own shares server-side.
export const transformLanesByPlant = (apiData) => {
  if (!apiData || !apiData.lanes || !apiData.lanes.length) return { rows: [], total: 0 };

  const byPlant = new Map();
  let grandTotalSpend = 0;

  apiData.lanes.forEach((lane) => {
    const name = _plantLabel(lane.origin);
    const spend = lane.spend || 0;
    const savings = lane.savings || 0;
    grandTotalSpend += spend;

    const existing = byPlant.get(name) || { name, spend: 0, savings: 0 };
    existing.spend += spend;
    existing.savings += savings;
    byPlant.set(name, existing);
  });

  const rows = Array.from(byPlant.values()).map((row) => ({
    name: row.name,
    spend: row.spend,
    sharePct: grandTotalSpend ? Number(((row.spend / grandTotalSpend) * 100).toFixed(1)) : 0,
    savings: row.savings,
    // same formula the backend uses for a single lane's savings_perc:
    // savings / (spend - savings) * 100
    savingsPct:
      row.spend - row.savings !== 0
        ? Number(((row.savings / (row.spend - row.savings)) * 100).toFixed(1))
        : 0,
  }));

  return { rows: rows.sort((a, b) => b.spend - a.spend), total: rows.length };
};